/**
 * Evaluation datasets list. Template example: NextAdmin `(dashboard)/(home)/_component/last-transactions-table` and
 * demo `/tables/data-tables` (`ListPage` + `DataTable`, row ⋯ menu); the create panel follows `profile/account/page.tsx`.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FlaskConical,
  ListChecks,
  Loader2,
  Play,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  deleteEvaluationDatasetMutation,
  listEvaluationDatasetsOptions,
  listEvaluationDatasetsQueryKey,
  runEvaluationDatasetMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import {
  ActionMenu,
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  useToast,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import type {
  EvaluationDataset,
  EvaluationDatasetRunResult,
  EvaluationRun,
} from '../../services/api/evaluation.api.js';
import { TARGET_TYPE_LABELS } from './evaluation-dataset-options.js';

interface DatasetRunStatus {
  status: EvaluationRun['status'] | EvaluationDatasetRunResult['status'];
}

const STATUS_BADGE: Record<string, { variant: 'success' | 'danger' | 'warning' | 'neutral' | 'info'; label: string }> =
  {
    queued: { variant: 'info', label: 'Queued' },
    running: { variant: 'warning', label: 'Running' },
    completed: { variant: 'success', label: 'Completed' },
    failed: { variant: 'danger', label: 'Failed' },
    pending: { variant: 'neutral', label: 'Pending' },
  };

const STATUS_ICON: Record<string, React.ReactNode> = {
  completed: <CheckCircle2 size={12} />,
  failed: <XCircle size={12} />,
  running: <Loader2 size={12} />,
};

export const EvaluationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [deleting, setDeleting] = useState<EvaluationDataset | null>(null);
  // Track run status per dataset
  const [runStatus, setRunStatus] = useState<Record<number, DatasetRunStatus>>({});

  const { data, isLoading, error, refetch } = useQuery(listEvaluationDatasetsOptions());
  const datasets = data?.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: listEvaluationDatasetsQueryKey() });

  const deleteMutation = useMutation({
    ...deleteEvaluationDatasetMutation(),
    onSuccess: async () => {
      notify('Dataset deleted');
      setDeleting(null);
      await refresh();
    },
    onError: () => notify('Could not delete the dataset', 'danger'),
  });

  const runMutation = useMutation({
    ...runEvaluationDatasetMutation(),
    onSuccess: (queued) => {
      setRunStatus((prev) => ({ ...prev, [queued.datasetId]: { status: queued.status } }));
      notify('Evaluation queued: the engine runs each case and saves the result when the last one is judged');
    },
    // A dataset with no runner is refused with its reason (only agent template datasets run).
    onError: (error) => notify(error instanceof Error ? error.message : 'Could not start the evaluation', 'danger'),
  });
  const runningId = runMutation.isPending ? Number(runMutation.variables?.path.id) : undefined;

  const columns: DataTableColumn<EvaluationDataset>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortValue: (ds) => ds.name,
      render: (ds) => <span className="font-medium text-heading">{ds.name}</span>,
    },
    {
      key: 'target',
      header: 'Target',
      render: (ds) => (
        <span className="flex items-center gap-2">
          <Badge variant="info" size="sm">
            {TARGET_TYPE_LABELS[ds.targetType]}
          </Badge>
          <span className="text-xs text-muted">{ds.targetId}</span>
        </span>
      ),
    },
    { key: 'cases', header: 'Cases', align: 'right', render: (ds) => ds.caseCount ?? 0 },
    {
      key: 'lastRun',
      header: 'Last run',
      render: (ds) => {
        const status = runStatus[ds.id]?.status;
        if (!status) return <span className="text-xs text-faint">Never</span>;
        const badge = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
        return (
          <Badge variant={badge.variant} size="sm" icon={STATUS_ICON[status] ?? <Clock size={12} />}>
            {badge.label}
          </Badge>
        );
      },
    },
    {
      key: 'created',
      header: 'Created',
      render: (ds) => (ds.createdAt ? new Date(ds.createdAt).toLocaleDateString() : '—'),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (ds) => (
        <Can policy="RequireSuperadmin">
          <ActionMenu
            aria-label={`Actions for ${ds.name}`}
            items={[
              {
                id: 'run',
                label: runningId === ds.id ? 'Running…' : 'Run evaluation',
                icon: <Play size={14} />,
                isDisabled: runningId === ds.id,
                onAction: () => runMutation.mutate({ path: { id: String(ds.id) } }),
              },
              {
                id: 'delete',
                label: 'Delete',
                icon: <Trash2 size={14} />,
                tone: 'danger',
                onAction: () => setDeleting(ds),
              },
            ]}
          />
        </Can>
      ),
    },
  ];

  const newButton = (
    <Can policy="RequireSuperadmin">
      <Link
        to="/admin/evaluations/new"
        className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
      >
        <Plus size={14} /> Create dataset
      </Link>
    </Can>
  );

  return (
    <>
      <ListPage
        title="Evaluations"
        subtitle="Test datasets and evaluation runs for agents, workflows, and tools."
        breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }, { label: 'Evaluations' }]}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/admin/evaluations/reasoning"
              className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
            >
              <ListChecks size={14} /> Reasoning evaluations
            </Link>
            {newButton}
          </div>
        }
        alert={
          error && !isLoading ? (
            <Callout
              tone="danger"
              icon={AlertTriangle}
              title="Failed to load evaluation datasets"
              actions={
                <Button size="sm" variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              Something went wrong fetching the datasets. Check your connection and try again.
            </Callout>
          ) : undefined
        }
      >
        {!error && (
          <DataTable
            aria-label="Evaluation datasets"
            columns={columns}
            rows={datasets}
            rowKey={(ds) => ds.id}
            loading={isLoading}
            pageSize={20}
            empty={
              <EmptyState
                icon={FlaskConical}
                title="No evaluation datasets yet"
                description="Create a dataset to start testing your agents and workflows."
                action={newButton}
              />
            }
          />
        )}
      </ListPage>
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate({ path: { id: String(deleting.id) } })}
        title="Delete dataset"
        message={`Delete the dataset "${deleting?.name ?? ''}" and its test cases? This cannot be undone.`}
        confirmLabel="Delete dataset"
        tone="danger"
        loading={deleteMutation.isPending}
      />
    </>
  );
};

export default EvaluationsPage;
